// client/src/coms/voip/videoClient.js
import { RoomEvent } from 'livekit-client';
import { setP2PScreenQuality, getP2PScreenEnabled, getP2PScreenTransport } from './p2pScreenShare';
import { setP2PCameraQuality, getP2PCameraEnabled, startP2PCameraShare, stopP2PCameraShare } from './p2pCameraShare';
import { getLocalScreenOn as getLivekitLocalScreenOn } from './voiceClient';
import { voiceState } from './voiceclient_parts/state';

const stateByRoom = new WeakMap();

function getState(room) {
  const r = room || null;
  if (!r) return null;

  let s = stateByRoom.get(r);
  if (!s) {
    s = {
      err: '',
      quality: 'thumb',
      boundQuality: false,
      disabled: true,
    };
    stateByRoom.set(r, s);
  }
  return s;
}

function normalizeQuality(q) {
  const v = String(q || '').trim();
  if (v === 'thumb') return 'thumb';
  if (v === 'expanded') return 'expanded';
  if (v === '720') return '720';
  if (v === '1080') return '1080';
  if (v === '1440') return '1440';
  if (v === '2160') return '2160';
  return 'thumb';
}

export function getLocalVideoMode() {
  return 'none';
}

export function getLocalCameraOn(room) {
  try {
    return !!getP2PCameraEnabled(room);
  } catch {
    return false;
  }
}

export function getLocalScreenOn(room) {
  try {
    if (getP2PScreenEnabled(room)) return true;
  } catch {}

  try {
    return !!getLivekitLocalScreenOn(room);
  } catch {
    return false;
  }
}

export function getLocalVideoErr(room) {
  const s = getState(room);
  return s ? String(s.err || '') : '';
}

export async function setLocalVideoMode() {
  return { ok: false, reason: 'livekit_video_disabled' };
}

export async function enableLocalCamera(room, { maxPeers = 4 } = {}) {
  const r = room || null;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };
  if (voiceState.permissions && voiceState.permissions.video === false) return { ok: false, reason: 'no_camera_permission' };
  try {
    return await startP2PCameraShare(r, { maxPeers });
  } catch (e) {
    return { ok: false, reason: String((e && e.message) || e || 'p2p_camera_failed') };
  }
}

export async function disableLocalCamera(room) {
  const r = room || null;
  if (!r) return { ok: true };
  try {
    return await stopP2PCameraShare(r);
  } catch {
    return { ok: true };
  }
}

export function pickParticipantVideoTrack() {
  return null;
}

export function setLocalVideoQuality(room, quality) {
  const r = room || null;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  const s = getState(r);
  if (!s) return { ok: false, reason: 'no_state' };

  const next = normalizeQuality(quality);
  if (s.quality === next) return { ok: true, already: true };

  s.quality = next;

  try {
    setP2PScreenQuality(r, next);
  } catch {}

  try {
    setP2PCameraQuality(r, next);
  } catch {}

  return { ok: true };
}

export function ensureVideoQualityBindings(room) {
  const r = room || null;
  if (!r) return;

  const s = getState(r);
  if (!s || s.boundQuality) return;

  s.boundQuality = true;

  const onData = (payload) => {
    let msg = null;
    try {
      msg = JSON.parse(new TextDecoder().decode(payload));
    } catch {
      return;
    }

    if (!msg || msg.__pa !== 'video_quality_v1') return;

    const me = String(r.localParticipant?.identity || r.localParticipant?.sid || '');
    const to = msg.to ? String(msg.to) : '';
    if (to && to !== me) return;

    const q = normalizeQuality(msg.q);
    try {
      setLocalVideoQuality(r, q);
    } catch {}
  };

  try {
    r.on(RoomEvent.DataReceived, onData);
  } catch {}
}

export function requestParticipantQuality(room, participant, quality) {
  const r = room || null;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  const p = participant || null;
  const to = String(p?.identity || '').trim();
  if (!to) return { ok: false, reason: 'no_target' };

  const out = {
    __pa: 'video_quality_v1',
    to,
    q: normalizeQuality(quality),
    ts: Date.now(),
  };

  try {
    const data = new TextEncoder().encode(JSON.stringify(out));
    try {
      const p = r.localParticipant.publishData(data, {
        reliable: true,
        destinationIdentities: [to],
      });
      if (p && typeof p.catch === "function") p.catch(() => {});
      return { ok: true };
    } catch {
      const p = r.localParticipant.publishData(data, { reliable: true });
      if (p && typeof p.catch === "function") p.catch(() => {});
      return { ok: true, broadcast: true };
    }
  } catch (e) {
    return {
      ok: false,
      reason: String((e && e.message) || e || 'publish_failed'),
    };
  }
}